/** Màn hình đăng nhập. */

import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { LogoSdkd } from '../components/icon/LogoSdkd';
import { useAuth } from '../lib/auth';
import { errorMessage } from '../lib/api';
import { Button, Field } from '../components/ui';

export default function LoginPage() {
  const { user, signIn, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { document.title = 'Đăng nhập — SDKD Chiếu sáng'; }, []);

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/dang-nhap' ? from : '/'} replace />;
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!username.trim() || !password) {
      setErr('Nhập đủ tên đăng nhập và mật khẩu');
      return;
    }
    setBusy(true);
    try {
      await signIn(username.trim(), password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/dang-nhap' ? from : '/', { replace: true });
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    // ⚠ Trước đây màn này là một dải xanh dương rực — đẹp, và **lạc hẳn** khỏi phần còn lại
    //   sau khi đổi nền tối: mở app lên là thấy hai phần mềm dán vào nhau, đúng thứ đã lấy
    //   làm lý do để đổi cả 9 trang trong MỘT đợt. Nay là hai tầng nền của hệ: nen-0 → nen-1.
    //   (⛔ chú thích JS, KHÔNG phải `{/* */}` — ở vị trí biểu thức thì JSX comment không hợp lệ.)
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-nen-0 to-nen-1 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center text-chu-1">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-nen-1/15">
            {/* TK-43: logo = dấu SDKD (cùng component với đầu trang; trước: tia điện lucide) */}
            <LogoSdkd className="h-8 w-8 text-nhan" />
          </div>
          <h1 className="text-xl font-semibold">Quản lý chiếu sáng đường phố</h1>
          <p className="mt-1 text-sm text-chu-2">Công ty SDKD</p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-2xl bg-nen-1 p-5 shadow-xl">
          <Field label="Tên đăng nhập" required>
            <input
              className="inp"
              value={username}
              autoComplete="username"
              autoCapitalize="none"
              autoFocus
              onChange={(e) => setUsername(e.target.value)}
              placeholder="vd: vanhanh01"
            />
          </Field>

          <Field label="Mật khẩu" required error={err ?? undefined}>
            <input
              className="inp"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Field>

          <Button type="submit" variant="primary" size="lg" block loading={busy} icon={<LogIn className="h-4 w-4" />}>
            Đăng nhập
          </Button>

          <p className="text-center text-xs text-chu-3">
            Quên mật khẩu? Liên hệ quản trị viên hệ thống để được cấp lại.
          </p>
        </form>
      </div>
    </div>
  );
}
